import * as v from 'valibot';

export const VIEW_PREFERENCES_VERSION = 1;

/** The list surfaces that carry per-user view preferences (v1). */
export type ViewPreferenceContext
  = | 'team-issues'
    | 'team-sprints'
    | 'requests'
  // A saved view's own display configuration, keyed by the view id. Two
  // contexts rather than one because the blob whitelist is per-surface: only
  // the requests side may carry a `views.table` section.
    | 'saved-view-issues'
    | 'saved-view-requests';

// `backlog` is only valid on the `team-issues` and `saved-view-issues`
// contexts server-side (apps/api/.../types/common.ts BACKLOG_LAYOUT_CONTEXTS)
// — the client stays lenient (see `blobSchema` below) and lets each
// consumer treat an inapplicable value as "not this surface" / fall back to
// its own default (see RequestsPage.vue).
export type ViewLayout = 'board' | 'table' | 'backlog';

// --- Requests-table display options (the `views.table` section) --------------
// Value whitelists mirror the server's transport contract
// (apps/api/src/modules/view-preferences/types/common.ts) — values live here,
// labels/icons stay in the requests module.

export const REQUEST_TABLE_GROUP_BY_FIELDS = [
  'status',
  'type',
  'priority',
  'project',
  'requester',
] as const;

export type RequestTableGroupBy = typeof REQUEST_TABLE_GROUP_BY_FIELDS[number];

/**
 * Orderings the table UI offers — a subset of `RequestsOrderBy`, and the
 * vocabulary of a sort chain's levels. `relevance_DESC` is search-implicit,
 * never stored.
 */
export const REQUEST_TABLE_ORDER_BY = [
  'number_ASC',
  'number_DESC',
  'name_ASC',
  'name_DESC',
  'priority_ASC',
  'priority_DESC',
  'status_ASC',
  'status_DESC',
  'project_ASC',
  'project_DESC',
  'type_ASC',
  'type_DESC',
  'requester_ASC',
  'requester_DESC',
  'createdAt_ASC',
  'createdAt_DESC',
  'releasedAt_ASC',
  'releasedAt_DESC',
  // Manual has no `_DESC`: "my order, backwards" is not a thing anyone asks a
  // list for, and offering it would just be a second way to be confused.
  'manual_ASC',
] as const;

export type RequestTableOrderBy = typeof REQUEST_TABLE_ORDER_BY[number];

/** Toggleable columns; `name` and the actions column are always visible. */
export const REQUEST_TABLE_COLUMNS = [
  'status',
  'type',
  'priority',
  'project',
  'requester',
  'created',
  'released',
] as const;

export type RequestTableColumnId = typeof REQUEST_TABLE_COLUMNS[number];

/** Code-default visible set: everything but `released` (the newcomer). */
export const DEFAULT_REQUEST_TABLE_COLUMNS: readonly RequestTableColumnId[] = [
  'status',
  'type',
  'priority',
  'project',
  'requester',
  'created',
];

export const DEFAULT_REQUEST_TABLE_SORT: readonly RequestTableOrderBy[] = ['createdAt_DESC'];

/**
 * The requests board's code-default ordering: most urgent first, with the
 * hand-made slot inside each tier — what the board always did before it had a
 * setting, so nobody's board moves until they pick something.
 */
export const DEFAULT_REQUEST_BOARD_SORT: readonly RequestTableOrderBy[] = ['priority_ASC'];

/** The field half of an ordering: `priority_ASC` → `priority`. */
export const sortLevelField = (level: RequestTableOrderBy): string => level.split('_')[0] ?? level;

/**
 * Bring a stored chain into the shape the server accepts: each field once (the
 * first level naming it wins) and `manual_ASC` only on its own — it decides
 * every row, so nothing after it could apply, and anything before it makes it
 * meaningless. Empty falls back to `fallback` — the table's code default
 * unless the surface names its own.
 */
export const normalizeSortChain = (
  chain: readonly RequestTableOrderBy[],
  fallback: readonly RequestTableOrderBy[] = DEFAULT_REQUEST_TABLE_SORT,
): RequestTableOrderBy[] => {
  if (chain[0] === 'manual_ASC') {
    return ['manual_ASC'];
  }

  const seen = new Set<string>();
  const levels = chain.filter((level) => {
    const field = sortLevelField(level);

    if (field === 'manual' || seen.has(field)) {
      return false;
    }

    seen.add(field);

    return true;
  });

  return levels.length > 0 ? levels : [...fallback];
};

const tableOptionsSchema = v.object({
  groupBy: v.optional(v.picklist(REQUEST_TABLE_GROUP_BY_FIELDS)),
  // The single ordering rows written before sort chains carry — read as a
  // one-level chain when `sort` is absent, never written any more.
  orderBy: v.optional(v.picklist(REQUEST_TABLE_ORDER_BY)),
  sort: v.optional(v.array(v.picklist(REQUEST_TABLE_ORDER_BY))),
  columns: v.optional(v.array(v.picklist(REQUEST_TABLE_COLUMNS))),
});

// The board's own section (`views.board`): the ordering only — see the API's
// `RequestBoardOptions` for why it is not the table's `sort`.
const boardOptionsSchema = v.object({
  sort: v.optional(v.array(v.picklist(REQUEST_TABLE_ORDER_BY))),
});

export type RequestBoardOptions = v.InferOutput<typeof boardOptionsSchema>;

/** The resolved board options — every key materialized. */
export interface ResolvedRequestBoardOptions {
  /** The sort chain, normalized. Never empty. */
  sort: RequestTableOrderBy[];
}

/** The stored shape — keys absent when the user never touched them. */
export type RequestTableOptions = v.InferOutput<typeof tableOptionsSchema>;

/** The resolved shape a consumer renders from — every key materialized. */
export interface ResolvedRequestTableOptions {
  groupBy: RequestTableGroupBy | null;
  /** The sort chain, normalized — see {@link normalizeSortChain}. Never empty. */
  sort: RequestTableOrderBy[];
  columns: RequestTableColumnId[];
}

// Unknown keys are stripped (not rejected): a row written by a future client
// with extra sections must still resolve its known sections here. The server
// is the strict side — it whitelists keys on write.
const blobSchema = v.object({
  v: v.literal(VIEW_PREFERENCES_VERSION),
  layout: v.optional(v.picklist(['board', 'table', 'backlog'])),
  views: v.optional(
    v.object({
      table: v.optional(tableOptionsSchema),
      board: v.optional(boardOptionsSchema),
    }),
  ),
});

export type ViewPreferencesBlob = v.InferOutput<typeof blobSchema>;

/**
 * Parse a JSON-serialized preferences blob coming from the server. Mirrors the
 * `filter-url.ts` philosophy for untrusted payloads: anything invalid —
 * malformed JSON, wrong shape, unsupported version — yields `null` so the
 * caller falls back to defaults. Never throws; a corrupt row must not break
 * the page.
 */
export const parseViewPreferences = (serialized: unknown): ViewPreferencesBlob | null => {
  if (typeof serialized !== 'string' || serialized.length === 0) {
    return null;
  }

  let raw: unknown;

  try {
    raw = JSON.parse(serialized);
  } catch {
    return null;
  }

  const result = v.safeParse(blobSchema, raw);

  return result.success ? result.output : null;
};
